import React from "react";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const shouldRenderAccessCode = (raw) => {
  const code = String(raw || "").trim();
  if (!code) return false;
  const normalized = code.toLowerCase();
  if (normalized === "(admin grant)" || normalized === "admin grant") return false;
  return true;
};

const StatusBadge = ({ status, willRenew, hasAccessGrant, hasScheduledChange }) => {
  if (hasAccessGrant) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
        Access Grant
      </span>
    );
  }
  // A pending plan switch (scheduled change) is NOT a cancellation. Show a
  // distinct, non-alarming badge instead of "Cancels at period end".
  if (status === "active" && hasScheduledChange) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
        Switching soon
      </span>
    );
  }
  if (status === "active" && willRenew === false) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        Cancels at period end
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
        Active
      </span>
    );
  }
  if (status === "trialing") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
        Trial
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${surfaces.tertiary} ${textColors.secondary}`}>
      {status || "Inactive"}
    </span>
  );
};

// Informational notice only. Any action (e.g. "Keep current plan instead",
// "Cancel subscription") is rendered by `SubscriptionLifecycleActions` below
// the available plans so the page has a single source of truth for what the
// user can do.
const UpcomingPlanCard = ({ upcoming, currentPlanDisplayName }) => {
  if (!upcoming) return null;
  const startDate = formatDate(upcoming.current_period_start);
  const upcomingName = upcoming.plan_display_name || upcoming.plan?.toUpperCase();
  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3">
      <div className="flex items-center gap-2">
        <CalendarIcon />
        <span className={`${typography.bodySmall} ${textColors.primary} font-medium`}>Upcoming plan</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
          Scheduled
        </span>
      </div>
      <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
        Your plan will switch to{" "}
        <span className={`${textColors.primary} font-medium`}>{upcomingName}</span>
        {startDate ? (
          <>
            {" "}on <span className={`${textColors.primary} font-medium`}>{startDate}</span>
          </>
        ) : null}
        . Until then, you keep all of your current{currentPlanDisplayName ? ` ${currentPlanDisplayName}` : ""} features.
      </div>
    </div>
  );
};

const AccessGrantNotice = ({ accessGrant, isPrimaryPlan }) => {
  if (!accessGrant?.is_active) return null;
  const accessEndsAt = formatDate(accessGrant?.expires_at);
  const accessCode = accessGrant?.code ? String(accessGrant.code) : null;

  return (
    <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
      <div className={`${typography.bodySmall} ${textColors.primary} font-medium`}>
        {isPrimaryPlan ? "Access coupon active" : "Access grant also active"}
      </div>
      <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
        {isPrimaryPlan
          ? "You have temporary access without adding a payment method."
          : "You also have an access grant providing supplementary plan access."
        }
        {" "}
        {accessEndsAt ? (
          <>
            Access ends on <span className={`${textColors.primary} font-medium`}>{accessEndsAt}</span>.
          </>
        ) : null}{" "}
        {isPrimaryPlan ? "Upgrade to a paid plan anytime to keep access after it ends." : "If your paid subscription ends, this grant will become your active plan."}
      </div>
      {shouldRenderAccessCode(accessCode) ? (
        <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Code: {accessCode}</div>
      ) : null}
    </div>
  );
};

const CurrentPlanCard = ({
  currentSubscription,
  currentPlan,
  accessGrant,
  upcomingSubscription,
}) => {
  if (!currentPlan) return null;

  const isPaid = Number(currentSubscription?.price || 0) > 0;
  const renewalDate = formatDate(currentSubscription?.current_period_end);
  const billingLabel = currentSubscription?.billing_period === "yearly" ? "year" : "month";
  const hasScheduledChange = Boolean(upcomingSubscription);
  const isFreePlan = currentPlan?.name === "free";
  const hasAccessGrant = Boolean(accessGrant?.is_active);
  const isAccessGrantPrimary = hasAccessGrant && !isPaid;
  const showEndOrRenewLine = isPaid && renewalDate && !isAccessGrantPrimary && !hasScheduledChange;

  return (
    <div className={`rounded-lg shadow-sm ${surfaces.primary} border ${borders.default} overflow-hidden`}>
      <div className={`px-4 sm:px-6 py-4 border-b ${borders.default}`}>
        <div className="flex items-center justify-between gap-3">
          <h2 className={`${typography.h5} ${textColors.primary}`}>Current Subscription</h2>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={`${typography.h4} ${textColors.primary}`}>{currentPlan.display_name}</h3>
                <StatusBadge
                  status={currentSubscription?.status}
                  willRenew={currentSubscription?.will_renew}
                  hasAccessGrant={isAccessGrantPrimary}
                  hasScheduledChange={hasScheduledChange}
                />
              </div>
              <p className={`${typography.body} ${textColors.secondary}`}>{currentPlan.description}</p>
            </div>

            <div className="sm:text-right">
              <div className={`${typography.h3} ${textColors.primary}`}>
                ${Number(currentSubscription?.price ?? currentPlan.monthly_price).toFixed(2)}
                <span className={`${typography.body} ${textColors.muted} font-normal`}>/{billingLabel}</span>
              </div>
            </div>
          </div>

          {!isFreePlan && currentPlan.highlights?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {currentPlan.highlights.map((highlight, index) => (
                <div key={index} className={`flex items-center ${typography.bodySmall} ${textColors.secondary}`}>
                  <CheckIcon />
                  <span className="ml-2">{highlight}</span>
                </div>
              ))}
            </div>
          ) : null}

          <AccessGrantNotice accessGrant={accessGrant} isPrimaryPlan={isAccessGrantPrimary} />

          {showEndOrRenewLine && (
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              {currentSubscription?.will_renew ? (
                <span>
                  Renews on <span className={`${textColors.primary} font-medium`}>{renewalDate}</span>.
                </span>
              ) : (
                <span>
                  Ends on <span className={`${textColors.primary} font-medium`}>{renewalDate}</span>. No upcoming plan.
                </span>
              )}
            </div>
          )}

          <UpcomingPlanCard
            upcoming={upcomingSubscription}
            currentPlanDisplayName={currentPlan?.display_name}
          />
        </div>
      </div>
    </div>
  );
};

export default CurrentPlanCard;


